import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import enewspaperApi from "../../libs/enewspaper"
import PaginationNew from "./components/PaginationNew"
import { formatThaiBuddhistDate } from "../../libs/dateUtils"
import { mainContainerStyle, headerSectionStyle, headerTitleStyle, tableStyle, thStyle, tdStyle } from "./styles/commonStyles"
import { COLORS, TYPOGRAPHY, SPACING, BORDER_RADIUS } from "./constants/theme"
import { getStatusTextStyle, snakeToTitleCase } from "./utils/helpers"

const DEFAULT_LIMIT = 10

const CancelReasonsList = () => {
	const [cancelReasons, setCancelReasons] = useState([])
	const [loading, setLoading] = useState(true)
	const [page, setPage] = useState(1)
	const [limit, setLimit] = useState(DEFAULT_LIMIT)
	const [totalPages, setTotalPages] = useState(1)
	const [total, setTotal] = useState(0)
	const [error, setError] = useState("")
	const controllerRef = useRef(null)

	const fetchCancelReasons = useCallback(async (targetPage, targetLimit) => {
		if (controllerRef.current) {
			controllerRef.current.abort()
		}

		const controller = new AbortController()
		controllerRef.current = controller

		setLoading(true)
		setError("")

		try {
			const data = await enewspaperApi.getCancelReasons(
				{
					page: targetPage,
					limit: targetLimit,
				},
				{ signal: controller.signal }
			)

			if (!data) {
				setCancelReasons([])
				setTotal(0)
				setTotalPages(1)
				setError("ไม่สามารถโหลดข้อมูลได้")
				return
			}

			const items = Array.isArray(data.cancelReasons) ? data.cancelReasons : []
			setCancelReasons(items)
			setTotal(typeof data.total === "number" ? data.total : items.length)
			setTotalPages(typeof data.totalPages === "number" ? data.totalPages : 1)
		} catch (err) {
			if (err.name === "CanceledError" || err.name === "AbortError") {
				return
			}
			console.error("Error fetching cancel reasons:", err)
			setCancelReasons([])
			setTotal(0)
			setTotalPages(1)
			setError("ไม่สามารถโหลดข้อมูลได้")
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchCancelReasons(page, limit)
	}, [fetchCancelReasons, page, limit])

	useEffect(() => {
		return () => {
			if (controllerRef.current) {
				controllerRef.current.abort()
			}
		}
	}, [])

	const handlePageChange = (newPage) => {
		const nextPage = Math.min(Math.max(1, newPage), totalPages || 1)
		setPage(nextPage)
	}

	const handleLimitChange = (newLimit) => {
		setLimit(newLimit > 0 ? newLimit : DEFAULT_LIMIT)
		setPage(1)
	}

	const tableStyleWide = {
		...tableStyle,
		minWidth: "1200px",
	}

	const tagStyle = {
		display: "inline-flex",
		alignItems: "center",
		padding: "2px 8px",
		borderRadius: BORDER_RADIUS.full,
		backgroundColor: COLORS.gray100,
		color: COLORS.gray700,
		fontSize: TYPOGRAPHY.fontSize.xs,
		fontWeight: TYPOGRAPHY.fontWeight.medium,
		border: `1px solid ${COLORS.gray200}`,
		whiteSpace: "nowrap",
	}

	const tagContainerStyle = {
		display: "flex",
		flexWrap: "wrap",
		gap: SPACING.xs,
	}

	const mutedTextStyle = {
		color: COLORS.gray400,
		fontStyle: "italic",
	}

	const renderDate = (value, format) => {
		if (!value) return "-"
		return formatThaiBuddhistDate(value, format)
	}

	return (
		<div style={mainContainerStyle}>
			<div style={headerSectionStyle}>
				<h2 style={headerTitleStyle}>ประวัติการยกเลิก</h2>
			</div>

			{error && (
				<div
					style={{
						background: COLORS.dangerLight,
						color: COLORS.danger,
						padding: `${SPACING.sm} ${SPACING.md}`,
						borderRadius: BORDER_RADIUS.md,
						marginBottom: SPACING.md,
						fontSize: TYPOGRAPHY.fontSize.sm,
						border: `1px solid ${COLORS.danger}`,
					}}
				>
					{error}
				</div>
			)}

			<div style={{ overflowX: "auto" }}>
				<table style={tableStyleWide}>
					<thead>
						<tr>
							<th style={thStyle}>#</th>
							<th style={thStyle}>วันที่ยกเลิก</th>
							<th style={thStyle}>รหัสลูกค้า</th>

							<th style={thStyle}>แพ็คเกจ</th>
							{/* <th style={thStyle}>User ID</th> */}
							{/* <th style={thStyle}>ประเภท</th> */}
							<th style={thStyle}>เหตุผล</th>
							<th style={thStyle}>อื่นๆ</th>
							<th style={thStyle}>สถานะตอนยกเลิก</th>
							<th style={thStyle}>วันเริ่มต้น - สิ้นสุด แพ็คเกจ</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={10} style={{ ...tdStyle, textAlign: "center", color: COLORS.gray500 }}>
									กำลังโหลด...
								</td>
							</tr>
						) : cancelReasons.length === 0 ? (
							<tr>
								<td colSpan={10} style={{ ...tdStyle, textAlign: "center", color: COLORS.gray500 }}>
									ไม่พบข้อมูลการยกเลิก
								</td>
							</tr>
						) : (
							cancelReasons.map((item, index) => {
								const globalIndex = (page - 1) * limit + index + 1
								const reasons = Array.isArray(item.reasons) ? item.reasons : []
								return (
									<tr key={item._id || `${item.customerId}-${index}`}>
										<td style={tdStyle}>{globalIndex}.</td>
										<td style={tdStyle}>{renderDate(item.createdAt)}</td>

										<td style={tdStyle}>
											{item.customerId ? (
												<Link to={`/enewspaper/customer/detail/${encodeURIComponent(item.customerId)}`}>{item.customerId}</Link>
											) : (
												<span style={mutedTextStyle}>-</span>
											)}
										</td>
										<td style={tdStyle}>{item.plan.name || <span style={mutedTextStyle}>-</span>}</td>
										{/* <td style={tdStyle}>{item.userId || <span style={mutedTextStyle}>-</span>}</td> */}
										{/* <td style={tdStyle}>{item.type ? snakeToTitleCase(item.type) : <span style={mutedTextStyle}>-</span>}</td> */}
										<td style={{ ...tdStyle, minWidth: 220, maxWidth: 300, whiteSpace: "normal" }}>
											{reasons.length === 0 ? (
												<span style={mutedTextStyle}>-</span>
											) : (
												<div style={tagContainerStyle}>
													{reasons.map((reason, reasonIndex) => (
														<span key={`${item._id || index}-${reason}-${reasonIndex}`} style={tagStyle}>
															{snakeToTitleCase(reason)}
														</span>
													))}
												</div>
											)}
										</td>
										<td style={{ ...tdStyle, minWidth: 180, whiteSpace: "pre-wrap" }}>{item.other ? item.other : <span style={mutedTextStyle}>-</span>}</td>
										<td style={tdStyle}>
											{item.statusAtCancel ? (
												<span style={getStatusTextStyle(item.statusAtCancel)}>{snakeToTitleCase(item.statusAtCancel)}</span>
											) : (
												<span style={mutedTextStyle}>-</span>
											)}
										</td>
										<td style={tdStyle}>
											{renderDate(item.startAt, "D MMM BB")} - {renderDate(item.expiredAt, "D MMM BB")}
										</td>
									</tr>
								)
							})
						)}
					</tbody>
				</table>
			</div>

			<PaginationNew page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={handlePageChange} onLimitChange={handleLimitChange} />
		</div>
	)
}

export default CancelReasonsList
